<template>
	<StoryblokComponent
		v-if="appStore.topBar"
		:blok="appStore.topBar"
		is-separator
		pause-on-hover
		class="text-xs"
	/>

	<div
		class="sticky top-0 z-[11] bg-tertiary h-[60px] text-primary"
		:class="{
			'border-b border-b-gray-lighter': pageType != 'home',
			'!bg-transparent !text-white': isTransparent
		}"
	>
		<header class="main-wrapper relative z-[11]">
			<!-- For Desktop -->
			<div class="header-desktop hidden lg:block text-sm">
				<LayoutHeaderDesktop
					v-if="appStore.headerLoaded && isDesktop"
					:top-navbar-left="appStore.topNavbarLeft"
					:cultures-norce="appStore.culturesNorce"
					:currencies-norce="appStore.currenciesNorce"
					:search="appStore.search"
					:site-logo="appStore.siteLogo"
					:site-logo-white="appStore.siteLogoWhite"
					:site-logo-dimensions="appStore.siteLogoDimensions"
					:menu="menu"
					:is-transparent-header="isTransparent"
				/>
			</div>
			<!-- For Mobile -->
			<div
				class="header-mobile block lg:hidden container mx-auto relative text-sm"
			>
				<LayoutHeaderMobile
					v-if="appStore.headerLoaded && isMobile"
					:top-navbar-left="appStore.topNavbarLeft"
					:cultures-norce="appStore.culturesNorce"
					:currencies-norce="appStore.currenciesNorce"
					:search="appStore.search"
					:site-logo="appStore.siteLogo"
					:site-logo-white="appStore.siteLogoWhite"
					:site-logo-dimensions="appStore.siteLogoDimensions"
					:menu="menu"
					:is-transparent-header="isTransparent"
				/>
			</div>
			<!-- Loading Spinner -->
			<div v-if="!appStore.headerLoaded" class="loader"></div>
		</header>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, watch } from "vue";
import { storeToRefs } from "pinia";
import { pickEffectiveMenu } from "~/templates/breadandboxers/utils/headerMenu";

const { pageType } = storeToRefs(usePageStore());
const cartStore = useCartStore();
const appStore = useAppStore();
const categoryStore = useCategoryStore();
const { locale } = useI18n();
const config = useRuntimeConfig();
const { kebabCase } = useNorcePwa();

// Storyblok MenuNode menu, or the legacy menu while it is empty. The category
// tree is already in the store (useInitApp), so fallback hrefs are complete on SSR.
const menu = computed(() =>
	pickEffectiveMenu(
		appStore.menu,
		appStore.menuCategoriesNorce,
		appStore.menuCategories,
		{
			categoryTree: categoryStore.getCategoryTree[locale.value] ?? [],
			cdnUrl: config.public.norce.cdn,
			kebabCase
		}
	)
);
const { isMobile, isDesktop } = useBreakpoints();
const { initCart } = useCartInit();

const isTransparent = ref(false);

let observer: IntersectionObserver | null = null;
let retryFrameId: number | null = null;
const intersectingTriggers = new Set<Element>();

const disconnectObserver = () => {
	if (observer) {
		observer.disconnect();
		observer = null;
	}
	if (retryFrameId !== null) {
		cancelAnimationFrame(retryFrameId);
		retryFrameId = null;
	}
	intersectingTriggers.clear();
	isTransparent.value = false;
};

const setupTransparentObserver = (attempt = 0) => {
	disconnectObserver();

	const triggers = document.querySelectorAll<HTMLElement>(
		"[data-transparent-header]"
	);

	if (triggers.length === 0) {
		if (attempt < 3) {
			retryFrameId = requestAnimationFrame(() => {
				setupTransparentObserver(attempt + 1);
			});
		} else {
			isTransparent.value = false;
		}
		return;
	}

	observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					intersectingTriggers.add(entry.target);
				} else {
					intersectingTriggers.delete(entry.target);
				}
			}
			isTransparent.value = intersectingTriggers.size > 0;
		},
		{
			rootMargin: "-47px 0px 0px 0px",
			threshold: 0
		}
	);

	triggers.forEach((el) => observer!.observe(el));
};

onMounted(() => {
	setupTransparentObserver();
});

onUnmounted(() => {
	disconnectObserver();
});

watch(pageType, async () => {
	await nextTick();
	setupTransparentObserver();
});

watch(
	() => cartStore.cartId,
	(newCartId) => {
		if (newCartId) initCart();
	},
	{ immediate: true }
);

watch(
	() => cartStore.updated,
	(newUpdated) => {
		if (newUpdated) initCart();
	},
	{ immediate: true }
);
</script>
